import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  People as PeopleIcon,
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  Work as WorkIcon,
  Error as ErrorIcon,
  Group as GroupIcon,
  AccountBalance as AccountBalanceIcon,
  HomeWork as HomeWorkIcon,
  InsertChart as InsertChartIcon,
  Hub as HubIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import LocalPoliceIcon from "@mui/icons-material/LocalPolice";
import BallotIcon from "@mui/icons-material/Ballot";
import { ThemeContext } from "../../utils/ThemeContext";

const Sidebar = ({
  userFullName,
  openPropertiesMenu,
  setOpenPropertiesMenu,
  hasPermission,
  isActive,
  handleSignOut,
  collapsed,
  setCollapsed,
}) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const { theme } = useContext(ThemeContext);

  const sidebarBg = theme.sidebarColor || "#26323d";
  const sidebarBorder = "rgba(255,255,255,0.08)";
  const hoverBg = "rgba(255,255,255,0.08)";
  const activeBg = "rgba(255,255,255,0.14)";
  const textColor = "#f4f7fb";
  const mutedText = "rgba(255,255,255,0.72)";

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) setDrawerOpen(false);
  };

  const onSignOutClick = async () => {
    if (isMobile) {
      setDrawerOpen(false);
    }

    if (typeof handleSignOut === "function") {
      await handleSignOut();
    }
  };

  const menuItems = [
    { label: "Home", icon: <DashboardIcon />, path: "/home", permission: "view_dashboard" },
    { label: "Users", icon: <PeopleIcon />, path: "/home/users", permission: "view_users" },
    { label: "Architectural Request", icon: <AccountBalanceIcon />, path: "/home/architecturalrequest", permission: "view_architectural_requests" },
    { label: "Violations", icon: <ErrorIcon />, path: "/home/violations", permission: "view_violations" },
    { label: "Work Orders", icon: <WorkIcon />, path: "/home/workorders", permission: "view_work_orders" },
    { label: "Visitor Tracking", icon: <LocalPoliceIcon />, path: "/home/GateSecurity", permission: "view_GateSecurity" },
    { label: "File Manager", icon: <FolderIcon />, path: "/home/filemanager", permission: "view_file_manager" },
    { label: "Reports", icon: <InsertChartIcon />, path: "/home/reports", permission: "view_reports" },
    { label: "Integrations", icon: <HubIcon />, path: "/home/integrations", permission: "reload_billable_requests" },
    { label: "Owners Page", icon: <GroupIcon />, path: "/home/memberspage", permission: "access_owners_page" },
    { label: "E-Voting", icon: <BallotIcon />, path: "/home/voting", permission: "access_voting" },
  ];

  const navButtonStyle = (active, isSub = false) => ({
    display: "flex",
    alignItems: "center",
    width: "100%",
    minHeight: isSub ? "42px" : "46px",
    padding: collapsed ? "10px 0" : isSub ? "8px 14px" : "10px 16px",
    justifyContent: collapsed ? "center" : "flex-start",
    background: active ? activeBg : "transparent",
    border: "none",
    color: textColor,
    cursor: "pointer",
    borderRadius: "12px",
    transition: "all 0.2s ease",
    marginBottom: "6px",
    fontSize: "0.95rem",
    fontWeight: active ? 700 : 500,
  });

  const renderPropertiesMembers = () => {
    if (!hasPermission("view_properties") && !hasPermission("view_members")) return null;

    if (collapsed) {
      return (
        <>
          {hasPermission("view_properties") && (
            <Tooltip title="Properties" placement="right">
              <button
                onClick={() => handleNavigate("/home/properties")}
                style={navButtonStyle(isActive("/home/properties"))}
                onMouseEnter={(e) => {
                  if (!isActive("/home/properties")) e.currentTarget.style.background = hoverBg;
                }}
                onMouseLeave={(e) => {
                  if (!isActive("/home/properties")) e.currentTarget.style.background = "transparent";
                }}
              >
                <HomeWorkIcon />
              </button>
            </Tooltip>
          )}

          {hasPermission("view_members") && (
            <Tooltip title="Members" placement="right">
              <button
                onClick={() => handleNavigate("/home/clubmembers")}
                style={navButtonStyle(isActive("/home/clubmembers"))}
                onMouseEnter={(e) => {
                  if (!isActive("/home/clubmembers")) e.currentTarget.style.background = hoverBg;
                }}
                onMouseLeave={(e) => {
                  if (!isActive("/home/clubmembers")) e.currentTarget.style.background = "transparent";
                }}
              >
                <GroupIcon />
              </button>
            </Tooltip>
          )}
        </>
      );
    }

    const isAnyActive = isActive("/home/properties") || isActive("/home/clubmembers");

    return (
      <>
        <Tooltip title="Properties & Members" placement="right">
          <button
            onClick={() => setOpenPropertiesMenu(!openPropertiesMenu)}
            style={navButtonStyle(isAnyActive)}
            onMouseEnter={(e) => {
              if (!isAnyActive) e.currentTarget.style.background = hoverBg;
            }}
            onMouseLeave={(e) => {
              if (!isAnyActive) e.currentTarget.style.background = "transparent";
            }}
          >
            <HomeWorkIcon />
            <span style={{ marginLeft: "12px", flexGrow: 1, textAlign: "left" }}>
              Properties & Members
            </span>
            {openPropertiesMenu ? <ExpandLess /> : <ExpandMore />}
          </button>
        </Tooltip>

        {openPropertiesMenu && (
          <Box sx={{ pl: 2, pr: 1, pb: 0.5 }}>
            {hasPermission("view_properties") && (
              <button
                onClick={() => handleNavigate("/home/properties")}
                style={navButtonStyle(isActive("/home/properties"), true)}
                onMouseEnter={(e) => {
                  if (!isActive("/home/properties")) e.currentTarget.style.background = hoverBg;
                }}
                onMouseLeave={(e) => {
                  if (!isActive("/home/properties")) e.currentTarget.style.background = "transparent";
                }}
              >
                <HomeWorkIcon sx={{ fontSize: 18 }} />
                <span style={{ marginLeft: "10px" }}>Properties</span>
              </button>
            )}

            {hasPermission("view_members") && (
              <button
                onClick={() => handleNavigate("/home/clubmembers")}
                style={navButtonStyle(isActive("/home/clubmembers"), true)}
                onMouseEnter={(e) => {
                  if (!isActive("/home/clubmembers")) e.currentTarget.style.background = hoverBg;
                }}
                onMouseLeave={(e) => {
                  if (!isActive("/home/clubmembers")) e.currentTarget.style.background = "transparent";
                }}
              >
                <GroupIcon sx={{ fontSize: 18 }} />
                <span style={{ marginLeft: "10px" }}>Members</span>
              </button>
            )}
          </Box>
        )}
      </>
    );
  };

  const SidebarContent = (
    <Box
      sx={{
        width: collapsed ? "84px" : "268px",
        background: `linear-gradient(180deg, ${sidebarBg} 0%, #202b35 100%)`,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        color: textColor,
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 1200,
        borderRight: `1px solid ${sidebarBorder}`,
        boxShadow: "8px 0 30px rgba(0,0,0,0.18)",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: collapsed ? 1 : 1.5,
          py: 1.5,
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#1b252d" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255,255,255,0.16)",
            borderRadius: "10px",
            border: "2px solid #1b252d",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(255,255,255,0.24)",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(255,255,255,0.16) #1b252d",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: collapsed ? "center" : "flex-end",
            alignItems: "center",
            mb: 1,
          }}
        >
          <IconButton
            onClick={() => setCollapsed((prev) => !prev)}
            sx={{
              color: textColor,
              backgroundColor: "rgba(255,255,255,0.06)",
              border: `1px solid ${sidebarBorder}`,
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.12)",
              },
            }}
          >
            {collapsed ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pb: 2,
            pt: 1,
          }}
        >
          <img
            src="/img/logo2.png"
            alt="MY HOA Logo"
            style={{
              width: collapsed ? "42px" : "108px",
              height: "auto",
              objectFit: "contain",
              filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.22))",
            }}
          />
        </Box>

        <Box>
          {menuItems.map(({ label, icon, path, permission }) => (
            <React.Fragment key={label}>
              {hasPermission(permission) && (
                <Tooltip title={collapsed ? label : ""} placement="right">
                  <button
                    onClick={() => handleNavigate(path)}
                    style={navButtonStyle(isActive(path))}
                    onMouseEnter={(e) => {
                      if (!isActive(path)) e.currentTarget.style.background = hoverBg;
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive(path)) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {icon}
                    {!collapsed && (
                      <span style={{ marginLeft: "12px", textAlign: "left" }}>{label}</span>
                    )}
                  </button>
                </Tooltip>
              )}

              {label === "Users" && renderPropertiesMembers()}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          px: collapsed ? 1 : 1.5,
          pb: 1.5,
          pt: 1,
          borderTop: `1px solid ${sidebarBorder}`,
          backgroundColor: "rgba(0,0,0,0.08)",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            mb: 1,
          }}
        >
          {!collapsed && (
            <Typography
              variant="body2"
              sx={{
                color: mutedText,
                fontWeight: 500,
                mb: 1,
                wordBreak: "break-word",
              }}
            >
              {userFullName}
            </Typography>
          )}

          <button
            onClick={onSignOutClick}
            style={{
              ...navButtonStyle(false),
              justifyContent: "center",
              marginBottom: 0,
              background: "rgba(255,255,255,0.05)",
              border: `1px solid ${sidebarBorder}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.05)";
            }}
          >
            <LogoutIcon />
            {!collapsed && (
              <span style={{ marginLeft: "10px", fontWeight: 600 }}>Sign Out</span>
            )}
          </button>
        </Box>

        {!collapsed && (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              textAlign: "center",
              width: "100%",
              color: mutedText,
              fontSize: "0.8rem",
              mt: 1.25,
            }}
          >
            © My HOA {new Date().getFullYear()}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <>
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{
              position: "fixed",
              top: 12,
              left: 12,
              zIndex: 1300,
              color: "#fff",
              backgroundColor: sidebarBg,
              border: `1px solid ${sidebarBorder}`,
              boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
              "&:hover": {
                backgroundColor: "#2d3a46",
              },
            }}
          >
            <MenuIcon />
          </IconButton>

          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            sx={{
              width: "268px",
              maxWidth: "100%",
              "& .MuiDrawer-paper": {
                width: "268px",
                maxWidth: "100%",
                background: `linear-gradient(180deg, ${sidebarBg} 0%, #202b35 100%)`,
                color: textColor,
                height: "100vh",
                borderRight: `1px solid ${sidebarBorder}`,
              },
            }}
          >
            {SidebarContent}
          </Drawer>
        </>
      ) : (
        SidebarContent
      )}
    </>
  );
};

export default Sidebar;
